import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'
import debounce from 'lodash.debounce'
import StoreSelectUI from './StoreSelectUI'
import { NO_STORE } from '../../models/stores'
import { logout, setStore } from '../../store/actions/auth-actions'
import {
  getSelectedStore,
  clearSelectedStore,
  searchCurrentUserRepresentatives,
  searchCurrentUserStores,
} from '../../store/actions/store-info-actions'
import { Roles } from '../../models/roles'
import {
  VALID_STORE_STATUSES,
  STORE_STATUS_UNKNOWN,
} from '../../models/constants'
import PageParamBuilder from '../../higher-order/pageable-table/page-param-builder'

const parseRepresentativeValue = ({ representative, currentUserRole }) => {
  const parts = []
  parts.push(`${representative.name}`)
  const tradeName =
    currentUserRole === Roles.RESURS_ADMIN
      ? `, ${representative.tradeName}`
      : ''
  parts.push(`(${representative.country}${tradeName})`)
  parts.push(representative.accountSystemChainId)
  return parts.join(' ')
}

const parseRepresentatives = ({ representatives, currentUserRole }) =>
  representatives.map(representative => ({
    key: representative.id,
    value: parseRepresentativeValue({ representative, currentUserRole }),
    name: representative.name,
    id: representative.id,
    country: representative.country,
    accountSystemChainId: representative.accountSystemChainId,
  }))

const parseStores = stores =>
  stores.map(store => ({
    key: store.id,
    value: `${store.name} (${store.accountSystemStoreId})`,
    name: store.name,
    id: store.id,
    country: store.country,
    accountSystemStoreId: store.accountSystemStoreId,
  }))

class StoreSelect extends Component {
  constructor(props) {
    super(props)
    this.state = {
      done: false,
      representatives: [],
      stores: [],
      selectedRepresentativeValue: null,
      selectedStoreValue: null,
      storeValid: false,
      page: Object.assign(new PageParamBuilder(), {
        size: 150,
      }),
    }

    this.searchRepresentatives()

    this.handleChangeRepresentativeSearchTerm =
      this.handleChangeRepresentativeSearchTerm.bind(this)
    this.handleChangeRepresentative = this.handleChangeRepresentative.bind(this)
    this.handleChangeStoreSearchTerm =
      this.handleChangeStoreSearchTerm.bind(this)
    this.handleChangeStore = this.handleChangeStore.bind(this)
    this.handleClearRepresentative = this.handleClearRepresentative.bind(this)
    this.handleClearStore = this.handleClearStore.bind(this)
    this.handleSave = this.handleSave.bind(this)
    this.handleCancel = this.handleCancel.bind(this)

    this.handleChangeRepresentativeSearchTerm = debounce(
      this.handleChangeRepresentativeSearchTerm,
      500,
    )
    this.handleChangeStoreSearchTerm = debounce(
      this.handleChangeStoreSearchTerm,
      500,
    )
  }

  handleCancel() {
    this.props.clearSelectedStore()
    const { isStoreSelected } = this.props
    if (isStoreSelected) {
      this.props.logout()
    } else {
      this.setState({ done: true })
    }
  }

  handleSave() {
    const {
      selectedRepresentativeValue: { id: representative },
      selectedStoreValue: { id: store },
    } = this.state
    this.props.setStore({ representative, store }).then(() => {
      this.setState({ done: true })
      this.props.clearSelectedStore()
      this.props.resetPaginationData()
    })
  }

  handleClearRepresentativeSearch() {
    this.setState({
      representatives: [],
    })
  }

  handleClearStoreSearch() {
    this.setState({
      stores: [],
    })
  }

  handleClearRepresentative() {
    this.setState({
      stores: [],
      selectedRepresentativeValue: null,
      selectedStoreValue: null,
      storeValid: false,
    })
    this.searchRepresentatives()
  }

  handleClearStore() {
    this.props.clearSelectedStore()
    this.setState({
      selectedStoreValue: null,
      storeValid: false,
    })
    const { selectedRepresentativeValue } = this.state
    if (selectedRepresentativeValue) {
      this.searchRepresentativeStores(selectedRepresentativeValue.id)
    }
  }

  handleChangeStore(stores) {
    this.handleChangeStoreSearchTerm.cancel()

    if (!!stores && stores.length) {
      const { id } = stores[0]
      const { selectedStoreValue } = this.state
      if (!selectedStoreValue || selectedStoreValue.id !== id) {
        this.props
          .getStore(id)
          .then(() => {
            const { currentSelectedStore } = this.props
            const { status = STORE_STATUS_UNKNOWN } = currentSelectedStore
            this.setState({
              selectedStoreValue: stores[0],
              storeValid: VALID_STORE_STATUSES.includes(status),
            })
          })
          .catch(() => {
            this.setState({
              selectedStoreValue: null,
              storeValid: false,
            })
          })
      }
    }
  }

  handleChangeStoreSearchTerm(searchTerm) {
    const { selectedRepresentativeValue } = this.state
    if (selectedRepresentativeValue) {
      this.searchRepresentativeStores(
        selectedRepresentativeValue.id,
        searchTerm,
      )
    }
  }

  handleChangeRepresentative(representatives) {
    this.handleChangeRepresentativeSearchTerm.cancel()

    if (!!representatives && representatives.length) {
      const { id } = representatives[0]
      const { selectedRepresentativeValue } = this.state
      if (
        !selectedRepresentativeValue ||
        selectedRepresentativeValue.id !== id
      ) {
        this.setState({ stores: [] })
        this.setState(
          {
            stores: [],
            selectedRepresentativeValue: representatives[0],
            selectedStoreValue: null,
          },
          () => {
            this.searchRepresentativeStores(representatives[0].id)
          },
        )
      }
    }
  }

  handleChangeRepresentativeSearchTerm(searchTerm) {
    this.searchRepresentatives(searchTerm)
  }

  searchRepresentativeStores(representativeId, searchTerm) {
    this.props
      .searchCurrentUserStores(representativeId, this.state.page, {
        search: searchTerm,
      })
      .then(() => {
        this.setState({
          stores: parseStores(this.props.stores),
        })
      })
  }

  searchRepresentatives(searchTerm) {
    this.props
      .searchCurrentUserRepresentatives(this.state.page, {
        search: searchTerm,
      })
      .then(() => {
        const { representatives, currentUserRole } = this.props
        this.setState({
          representatives: parseRepresentatives({
            representatives,
            currentUserRole,
          }),
        })
      })
  }

  render() {
    const {
      done,
      representatives,
      stores,
      selectedRepresentativeValue,
      selectedStoreValue,
      storeValid,
    } = this.state
    const { store } = this.props
    if (done && store !== NO_STORE) {
      return <Redirect to="/" />
    }
    return (
      <StoreSelectUI
        representatives={representatives}
        stores={stores}
        selectedRepresentative={selectedRepresentativeValue}
        selectedStore={selectedStoreValue}
        storeValid={storeValid}
        onChangeRepresentativeSearchTerm={searchTerm => {
          this.handleClearRepresentativeSearch()
          this.handleChangeRepresentativeSearchTerm(searchTerm)
        }}
        onChangeRepresentative={this.handleChangeRepresentative}
        onChangeStoreSearchTerm={searchTerm => {
          this.handleClearStoreSearch()
          this.handleChangeStoreSearchTerm(searchTerm)
        }}
        onChangeStore={this.handleChangeStore}
        onClearRepresentative={this.handleClearRepresentative}
        onClearStore={this.handleClearStore}
        onSaveClick={this.handleSave}
        onCancelClick={this.handleCancel}
      />
    )
  }
}

StoreSelect.propTypes = {
  store: PropTypes.string.isRequired,
  representatives: PropTypes.array.isRequired,
  stores: PropTypes.array.isRequired,
  searchCurrentUserRepresentatives: PropTypes.func.isRequired,
  searchCurrentUserStores: PropTypes.func.isRequired,
  setStore: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  isStoreSelected: PropTypes.bool.isRequired,
  currentUserRole: PropTypes.string.isRequired,
  getStore: PropTypes.func.isRequired,
  clearSelectedStore: PropTypes.func.isRequired,
  currentSelectedStore: PropTypes.object,
  resetPaginationData: PropTypes.func.isRequired,
}

StoreSelect.defaultProps = {
  currentSelectedStore: null,
}

const mapStateToProps = state => ({
  representatives: state.storeInfo.representatives,
  stores: state.storeInfo.stores,
  isStoreSelected: state.auth.selectStore,
  store: state.auth.store.storeUID,
  currentUserRole: state.auth.role,
  currentSelectedStore: state.storeInfo.currentSelectedStore,
})

const mapDispatchToProps = dispatch => ({
  searchCurrentUserRepresentatives: (pageInfo, searchInfo) =>
    dispatch(searchCurrentUserRepresentatives(pageInfo, searchInfo)),
  searchCurrentUserStores: (representativeId, pageInfo, searchInfo) =>
    dispatch(searchCurrentUserStores(representativeId, pageInfo, searchInfo)),
  getStore: storeId => dispatch(getSelectedStore(storeId)),
  clearSelectedStore: () => dispatch(clearSelectedStore()),
  setStore: storeInfo => dispatch(setStore(storeInfo)),
  logout: () => {
    dispatch(logout())
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(StoreSelect)
